<script>
  import { _ } from "svelte-i18n";
  import { init } from "svelte-i18n";
  import { router } from "tinro";
  import { BASE_URL } from "../utilities/config.js";
  import { onMount } from "svelte";
  import { api } from "../utilities/Utils.svelte";
  import {
    currentPage,
    userId,
    userName,
    assessmentId,
    assessmentState,
    termsAndConditionOpen,
    privacyPolicyOpen,
    activationCode,
    invitationNumber,
    readinessCheckCompleted,
  } from "../utilities/DataStore.js";
  import TermsAndConditions from "./components/TermsAndConditions.svelte";
  import PrivacyPolicy from "./components/PrivacyPolicy.svelte";

  let errorMsg = undefined;
  let loadingState = "NEW";

  // Only IELTS is enabled for now.
  const TEST_CATEGORIES = [
    { value: "IELTS", enabled: true },
    { value: "TOEIC", enabled: false },
    { value: "TOEFL", enabled: false },
  ];

  // Test versions per category. The selected version is used as the
  // activation code and redeemed server-side (same as GJUN_TOEIC).
  const TEST_VERSIONS = {
    IELTS: ["GJUN_IELTS_1", "GJUN_IELTS_2", "GJUN_IELTS_3"],
    TOEIC: [],
    TOEFL: [],
  };

  let userIdValue;
  let testCategory = "IELTS";
  let testVersion = "GJUN_IELTS_1";
  let invitationNumberValue;

  let isValidUserId;
  let isValidTestCategory;
  let isValidTestVersion;
  let submitTry = false;

  $: availableVersions = TEST_VERSIONS[testCategory] || [];

  function isEnabledCategory(value) {
    return TEST_CATEGORIES.some((c) => c.value === value && c.enabled);
  }

  onMount(async () => {
    // The GJun landing page is English-only.
    init({
      fallbackLocale: "en",
      initialLocale: "en-US",
    });

    const url = new URL(window.location.href);

    // Pre-fill from URL query params, e.g.
    // ?userId=abc123&testCategory=IELTS&testVersion=GJUN_IELTS_2
    const userIdParam = url.searchParams.get("userId");
    const categoryParam = url.searchParams.get("testCategory");
    const versionParam = url.searchParams.get("testVersion");

    if (userIdParam && userIdParam.trim()) {
      userIdValue = userIdParam.trim();
    }

    if (categoryParam && isEnabledCategory(categoryParam)) {
      testCategory = categoryParam;
    }

    let versionValid = false;
    if (versionParam) {
      // Derive the category from the version if it wasn't (validly) provided.
      const categoryOfVersion = Object.keys(TEST_VERSIONS).find((c) =>
        TEST_VERSIONS[c].includes(versionParam),
      );
      if (categoryOfVersion && isEnabledCategory(categoryOfVersion)) {
        testCategory = categoryOfVersion;
        testVersion = versionParam;
        versionValid = true;
      }
    }

    invitationNumberValue =
      url.searchParams.get("invitationNumber") || url.searchParams.get("i");
    console.log("invitationNumber", invitationNumberValue);

    // If everything needed came in via the URL and is valid, continue
    // automatically without requiring the user to press the button.
    if (userIdValue && versionValid) {
      startAssessment();
    }
  });

  function makeid(length) {
    var result = "";
    var characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  function onCategoryChange() {
    // Keep the version consistent with the selected category.
    if (!availableVersions.includes(testVersion)) {
      testVersion = availableVersions[0];
    }
  }

  function startAssessment() {
    submitTry = true;

    isValidUserId = !!(userIdValue && userIdValue.trim());
    isValidTestCategory = isEnabledCategory(testCategory);
    isValidTestVersion = !!(
      testVersion && (TEST_VERSIONS[testCategory] || []).includes(testVersion)
    );

    if (isValidUserId && isValidTestCategory && isValidTestVersion) {
      userIdValue = userIdValue.trim();

      $assessmentId = makeid(7);
      $userId = userIdValue;
      $userName = userIdValue;
      $activationCode = testVersion;

      errorMsg = undefined;
      loadingState = "LOADING";

      redeemAndContinue();
    }
  }

  // Redeems the activation code (= selected test version) via the backend.
  // On success an invitation exists and we continue into the assessment;
  // otherwise the code was not found or already used and we surface an error.
  async function redeemAndContinue() {
    $invitationNumber = undefined;

    try {
      await checkOrCreateInvitation();
    } catch (e) {
      console.log("redeem error", e);
    }

    if (!$invitationNumber) {
      isValidTestVersion = false;
      errorMsg = $_("ACTIVATION_CODE_INVALID_OR_USED");
      loadingState = "NEW";
      return;
    }

    $assessmentState = "START";
    $readinessCheckCompleted = false;

    try {
      const DONE_STATUSES = ["SCORED", "DONE", "SUBMITTED", "ENDED", "CANCEL", "EXPIRED"];
      const defs = await api
        .get(
          `public/assessment/invitation/${$invitationNumber}/definitions-detail`,
        )
        .json();
      const allDone =
        defs.length > 0 &&
        defs.every(
          (d) =>
            DONE_STATUSES.includes(d.invDefStatus) ||
            DONE_STATUSES.includes(d.status),
        );
      if (allDone) {
        $currentPage = "AssessmentSelection";
        return;
      }
    } catch (e) {
      // fall through to IntroductionPage
    }

    $currentPage = "IntroductionPage";
  }

  let invitationData;
  async function checkOrCreateInvitation() {
    if (invitationNumberValue) {
      await fetch(
        BASE_URL +
          `/public/assessment/invitation2/${invitationNumberValue}?clientId=${encodeURIComponent(userIdValue)}`,
      )
        .then((response) => {
          if (!response.ok) {
            console.log("response error");
            throw new Error("Network response was not ok");
          }
          console.log("response ok", response);
          return response.json();
        })
        .then((data) => {
          invitationData = data;
          $invitationNumber = data?.invitationNumber;
          router.goto("/invitation/" + $invitationNumber);
        })
        .catch(async (error) => {
          console.log("catch error", error);
        });
    } else {
      // Try to resume an existing invitation for this userId first.
      try {
        const response = await api.get(`public/assessment/invitation2`, {
          searchParams: { clientId: userIdValue },
        });

        invitationData = await response.json();
        // invitation2 returns an empty DTO (no invitationNumber) when nothing
        // exists for this userId — only resume when we actually found one.
        if (invitationData?.invitationNumber) {
          $invitationNumber = invitationData.invitationNumber;
          router.goto("/invitation/" + $invitationNumber);
        }
      } catch (error) {
        console.log("catch error - no existing invitation found", error);
      }

      // Create a new one if we didn't find a previous one.
      if (!$invitationNumber) {
        const payload = {
          clientName: userIdValue,
          clientId: userIdValue,
          assessmentType: testCategory,
          invitationNumber: makeid(8),
          activationCode: $activationCode,
          status: "NEW",
          data: JSON.stringify({}),
        };

        await fetch(
          BASE_URL +
            `/public/assessment/invitation?activationCode=${$activationCode}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        )
          .then((response) => {
            if (!response.ok) {
              console.log("response error");
              throw new Error("Network response was not ok");
            }
            console.log("response ok", response);
            return response.json();
          })
          .then((data) => {
            invitationData = data;
            $invitationNumber = invitationData?.invitationNumber;
            router.goto("/invitation/" + $invitationNumber);
          })
          .catch((error) => {
            console.log("catch error", error);
          });
      }
    }
  }
</script>

<TermsAndConditions />
<PrivacyPolicy />
<div class="container landing-root">
  <div class="my-5 text-center landing-shell">
    <div class="container col-xl-12 col-xxl-10 px-4 py-1 landing-inner-shell">
      <div class="row align-items-start g-lg-5 py-1">
        <div
          class="col-lg-7 text-left align-items-start text-lg-start mb-3 mt-4"
        >
          <div class="mb-4" style="margin-top: 2em;">
            <span
              ><a
                href="https://www.oksensay.com/"
                target="_blank"
                rel="noopener noreferrer"
                ><img
                  style="margin-right: 1em;"
                  src="assets/img/logo_horizontal_nobg2.png"
                  alt="Sensay"
                /></a
              ></span
            >
          </div>
          <h3 class="display-6 fw-bold lh-1 mb-3">
            GJun Assessment – Test Page
          </h3>

          <div class="alert alert-warning text-left" role="alert">
            <strong>Test page:</strong> This is a test version of the English
            speaking assessment for GJun, intended for evaluation and
            integration testing only. Results do not represent an official
            assessment.
          </div>

          <p class="text-left mb-2">
            You can pre-fill this form by passing the details as URL query
            parameters:
          </p>

          <ul class="text-left mb-3">
            <li>
              <code>userId</code> – any text or number identifying the test
              taker
            </li>
            <li>
              <code>testCategory</code> – <code>IELTS</code> (TOEIC and TOEFL
              coming soon)
            </li>
            <li>
              <code>testVersion</code> – <code>GJUN_IELTS_1</code>,
              <code>GJUN_IELTS_2</code> or <code>GJUN_IELTS_3</code>
            </li>
          </ul>

          <p class="text-left mb-2">Example:</p>
          <pre
            class="text-left param-example">?userId=student123&amp;testCategory=IELTS&amp;testVersion=GJUN_IELTS_2</pre>

          <p class="text-left mb-3">
            When a <code>userId</code> and a valid <code>testVersion</code> are
            provided in the URL, the assessment continues automatically without
            pressing the button.
          </p>
        </div>
        <div
          class="col-12 col-sm-12 col-md-10 mx-auto col-lg-5 landing-form-col"
        >
          <form class="p-4 p-md-5 border rounded-3 bg-light">
            <h4 class="mb-4">Start a GJun Test Assessment</h4>

            <div class="form-floating mb-3">
              <label for="userIdInput">{$_("USER_ID")}</label><input
                type="text"
                bind:value={userIdValue}
                class="form-control"
                id="userIdInput"
                placeholder=""
                class:is-valid={submitTry && isValidUserId}
                class:is-invalid={submitTry && !isValidUserId}
              />
              <div class="valid-feedback" />
              <div class="invalid-feedback">{$_("USER_ID_VALIDATION")}</div>
            </div>

            <div class="form-floating mb-3">
              <label for="testCategorySelect">{$_("TEST_CATEGORY")}</label>
              <select
                class="form-control"
                id="testCategorySelect"
                bind:value={testCategory}
                on:change={onCategoryChange}
                class:is-valid={submitTry && isValidTestCategory}
                class:is-invalid={submitTry && !isValidTestCategory}
              >
                {#each TEST_CATEGORIES as category}
                  <option value={category.value} disabled={!category.enabled}>
                    {category.value}{category.enabled
                      ? ""
                      : ` (${$_("COMING_SOON")})`}
                  </option>
                {/each}
              </select>
              <div class="valid-feedback" />
              <div class="invalid-feedback">
                {$_("TEST_CATEGORY_VALIDATION")}
              </div>
            </div>

            <div class="form-floating mb-3">
              <label for="testVersionSelect">{$_("TEST_VERSION")}</label>
              <select
                class="form-control"
                id="testVersionSelect"
                bind:value={testVersion}
                class:is-valid={submitTry && isValidTestVersion}
                class:is-invalid={submitTry && !isValidTestVersion}
              >
                {#each availableVersions as version}
                  <option value={version}>{version}</option>
                {/each}
              </select>
              <div class="valid-feedback" />
              <div class="invalid-feedback">
                {$_("TEST_VERSION_VALIDATION")}
              </div>
            </div>

            {#if loadingState === "NEW"}
              <button
                class="w-100 btn btn-lg btn-primary mt-4 choose-assessment-btn"
                type="button"
                on:click={startAssessment}>{$_("CHOOSE_ASSESSMENT")}</button
              >
            {:else}
              <div class="spinner-border align-center" role="status">
                <span class="sr-only">{$_("LOADING")}</span>
              </div>
            {/if}

            {#if errorMsg}
              <div class="alert alert-danger mt-3" role="alert">{errorMsg}</div>
            {/if}

            <hr class="my-4" />
            {$_("TERMS_PART1")}<button
              class="btn btn-link p-0 senasy-link"
              on:click|preventDefault={() => ($termsAndConditionOpen = true)}
              >{$_("TERMS_PART2")}</button
            >
            {$_("TERMS_PART3")}
            <button
              class="btn btn-link p-0 senasy-link"
              on:click|preventDefault={() => ($privacyPolicyOpen = true)}
              >{$_("TERMS_PART4")}</button
            >
          </form>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .senasy-link,
  .senasy-link:hover,
  .senasy-link:focus {
    color: #00a094ff !important;
  }

  .choose-assessment-btn {
    font-size: 22px !important;
    white-space: normal;
    line-height: 1.2;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  @media (max-width: 360px) {
    .choose-assessment-btn {
      font-size: 18px !important;
    }
  }

  @media (max-width: 991.98px) {
    .landing-root {
      width: 90vw !important;
      max-width: 90vw !important;
      margin-left: auto !important;
      margin-right: auto !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .landing-shell {
      width: 100%;
      max-width: 100%;
      margin-left: auto;
      margin-right: auto;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .landing-inner-shell {
      width: 100% !important;
      max-width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    .landing-inner-shell > .row {
      --bs-gutter-x: 0;
      margin-left: 0;
      margin-right: 0;
    }

    .landing-inner-shell > .row > [class*="col-"] {
      padding-left: 0;
      padding-right: 0;
    }

    .landing-form-col {
      flex: 0 0 100% !important;
      max-width: 100% !important;
      width: 100% !important;
      padding-left: 0;
      padding-right: 0;
    }

    .landing-form-col > form {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
      padding: 0.75rem !important;
    }
  }

  h3 {
    font-family: "Noto Sans", serif;
    font-weight: 800;
    font-size: xx-large;
  }

  .param-example {
    background-color: #f1f3f5;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
    white-space: pre-wrap;
    word-break: break-all;
    font-size: 0.85rem;
  }

  h4 {
    text-align: left;
    font-family: "Noto Sans", serif;
    font-weight: 500;
  }

  label {
    text-align: left;
    text-transform: uppercase;
    font-size: 0.85rem;
  }

  p {
    font-family: "Noto Sans", serif;
  }
</style>
